// ============================================
// pages/release-orders/ReleaseOrdersDashboard.tsx — Release Orders list (real API)
// Downstream sales: MARKFED releases procured stock to FCI / civil supplies / private buyers.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Plus, Edit2, CheckCircle, XCircle, ShieldCheck, Send, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seasonsAPI, commoditiesAPI, godownsAPI, releaseOrdersAPI } from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',            cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' },
  submitted:        { label: 'Submitted',        cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  dm_verified:      { label: 'DM Verified',      cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  manager_approved: { label: 'Approved',         cls: 'bg-green-50 text-green-700 border-green-300' },
  closed:           { label: 'Closed',           cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  rejected:         { label: 'Rejected',         cls: 'bg-red-50 text-red-700 border-red-300' },
};

export const ReleaseOrdersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canSubmit = hasRole(UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (commodityFilter) params.commodity_id = commodityFilter;
        const res = await releaseOrdersAPI.list(selectedSeasonId, params);
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load orders' });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, commodityFilter]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter((o) => (o.status || 'draft') === statusFilter);
  }, [orders, statusFilter]);

  const advance = async (id: number, fn: () => Promise<any>, next: string, msg: string) => {
    setActionLoading(id);
    setMessage(null);
    try {
      await fn();
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: next } : o)));
      setMessage({ type: 'success', text: msg });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed: ${msg}` });
    } finally {
      setActionLoading(null);
    }
  };

  const doSubmit = (id: number) => window.confirm('Submit RO for verification?')
    && advance(id, () => releaseOrdersAPI.submit(selectedSeasonId, id), 'submitted', 'Submitted');
  const doVerify = (id: number) => window.confirm('Verify this RO?')
    && advance(id, () => releaseOrdersAPI.verify(selectedSeasonId, id), 'dm_verified', 'Verified');
  const doApprove = (id: number) => window.confirm('Approve as MD?')
    && advance(id, () => releaseOrdersAPI.approve(selectedSeasonId, id), 'manager_approved', 'Approved');
  const doClose = (id: number) => window.confirm('Close this RO (buyer has paid in full)?')
    && advance(id, () => releaseOrdersAPI.close(selectedSeasonId, id), 'closed', 'Closed');
  const doReject = async (id: number) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason?.trim()) return;
    await advance(id, () => releaseOrdersAPI.reject(selectedSeasonId, id, reason.trim()), 'rejected', 'Rejected');
  };

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading && orders.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-purple-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-7 h-7 text-purple-600" />
            Release Orders
          </h1>
          <p className="text-sm text-gray-500 mt-1">Downstream sales — release procured stock to FCI / civil supplies / private buyers</p>
        </div>
        <button onClick={() => navigate('/release-orders/generate')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg text-sm">
          <Plus className="w-4 h-4" />Generate RO
        </button>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="dm_verified">DM Verified</option>
              <option value="manager_approved">Approved</option>
              <option value="closed">Closed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">RO No</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">RO Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Buyer</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Qty (MT)</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Total</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Received</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No release orders. Click "Generate RO".</td></tr>
              ) : filtered.map((o, i) => {
                const st = o.status || 'draft';
                const cfg = STATUS_CONFIG[st] || { label: st, cls: 'bg-gray-100 text-gray-500 border-gray-300' };
                const isLoading = actionLoading === o.id;
                return (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{o.ro_number}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(o.ro_date)}</td>
                    <td className="px-3 py-2 text-gray-700">{o.buyer_name}</td>
                    <td className="px-3 py-2 text-gray-600 uppercase">{o.buyer_type || '--'}</td>
                    <td className="px-3 py-2 text-gray-700">{o.commodity?.name || '--'}</td>
                    <td className="px-3 py-2 text-right">{num(o.quantity_mts).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatAmount(num(o.total_value))}</td>
                    <td className="px-3 py-2 text-right text-green-700">{formatAmount(num(o.payment_received))}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {(st === 'draft' || st === 'rejected') && (
                          <button onClick={() => navigate(`/release-orders/edit/${o.id}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded">
                            <Edit2 className="w-3 h-3" />Edit
                          </button>
                        )}
                        {st === 'draft' && canSubmit && (
                          <button onClick={() => doSubmit(o.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}Submit
                          </button>
                        )}
                        {st === 'submitted' && canVerify && (
                          <button onClick={() => doVerify(o.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded disabled:opacity-50">
                            <ShieldCheck className="w-3 h-3" />Verify
                          </button>
                        )}
                        {st === 'dm_verified' && canApprove && (
                          <button onClick={() => doApprove(o.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50">
                            <CheckCircle className="w-3 h-3" />Approve
                          </button>
                        )}
                        {st === 'manager_approved' && canApprove && (
                          <button onClick={() => doClose(o.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-gray-600 hover:bg-gray-700 rounded disabled:opacity-50">
                            <Lock className="w-3 h-3" />Close
                          </button>
                        )}
                        {['submitted', 'dm_verified'].includes(st) && (canVerify || canApprove) && (
                          <button onClick={() => doReject(o.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50">
                            <XCircle className="w-3 h-3" />Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReleaseOrdersDashboard;
